import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeadphonesIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
interface OrderBumpProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}
export function OrderBump({
  checked,
  onCheckedChange
}: OrderBumpProps) {
  const [expanded, setExpanded] = useState(false);
  const {
    language
  } = useLanguage();
  const isEs = language === 'es';
  return <div className="border-[3px] border-dashed border-[#E8B86D] rounded-2xl overflow-hidden bg-gradient-to-br from-[#1F3329] to-[#162820]" data-id="element-50">
      {/* Header banner */}
      <div className="bg-gradient-to-r from-[#E8B86D] via-[#D4A574] to-[#C87941] px-4 py-2 text-center" data-id="element-51">
        <p className="text-[#2C4A3E] text-xs md:text-sm font-bold uppercase tracking-widest" data-id="element-52">
          {isEs ? '⚡ Mejora tu pedido — Oferta única' : '⚡ Order Bump — One-time offer'}
        </p>
      </div>

      {/* Content */}
      <div className="p-4 md:p-6" data-id="element-53">
        <div className="flex gap-4 md:gap-6 items-start" data-id="element-54">
          {/* Product image */}
          <div className="flex-shrink-0 w-28 md:w-36" data-id="element-55">
            <img src="https://cdn.magicpatterns.com/uploads/5G4iAL7kNi1JJtPsinFJui/ChatGPT_Image_22_feb_2026,_23_03_16.png" alt="Reprogramación para recibir sin culpa" className="w-full rounded-xl shadow-lg" loading="lazy" decoding="async" data-id="element-56" />
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0" data-id="element-57">
            {/* Audio badge */}
            <div className="flex items-center gap-1.5 mb-2" data-id="element-58">
              <HeadphonesIcon className="w-3.5 h-3.5 text-[#E8B86D]" data-id="element-59" />
              <span className="text-[10px] md:text-xs uppercase tracking-widest text-[#E8B86D] font-bold" data-id="element-60">
                {isEs ? 'Audio guiado' : 'Guided audio'}
              </span>
            </div>

            <h3 className="font-serif text-base md:text-xl text-[#FBF8F3] font-bold leading-tight mb-3" data-id="element-61">
              {isEs ? 'Reprogramación para Recibir sin Culpa' : 'Reprogramming to Receive Without Guilt'}
            </h3>

            {/* Collapsible description */}
            <div className="mb-3" data-id="element-62">
              <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1.5 text-[#D4A574] hover:text-[#E8B86D] transition-colors duration-200 text-xs md:text-sm font-semibold" data-id="element-63">
                <span data-id="element-64">{isEs ? 'Ver descripción' : 'View description'}</span>
                <motion.div animate={{
                rotate: expanded ? 180 : 0
              }} transition={{
                duration: 0.2
              }} data-id="element-65">
                  <ChevronDownIcon className="w-3.5 h-3.5" data-id="element-66" />
                </motion.div>
              </button>

              <AnimatePresence data-id="element-67">
                {expanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-68">
                    <div className="mt-3 bg-[#1A3028]/60 border border-[#D4A574]/20 rounded-xl p-3 md:p-4 space-y-2.5" data-id="element-69">
                      <p className="text-xs md:text-sm text-[#D4C4A8] leading-relaxed italic" data-id="element-70">
                        {isEs ? 'Si el tema del dinero activa algo en tu cuerpo… este audio es para ti.' : 'If the topic of money activates something in your body… this audio is for you.'}
                      </p>
                      <p className="text-xs md:text-sm text-[#E8D5B5] font-semibold" data-id="element-71">
                        {isEs ? 'Una guía íntima de 10 minutos, un shot de franqueza para:' : 'An intimate 10-minute guide, a shot of honesty to:'}
                      </p>
                      <ul className="space-y-1.5" data-id="element-72">
                        {(isEs ? ['Liberar culpa heredada al recibir', 'Regular tu sistema nervioso cuando el dinero aparece', 'Sentirte segura al expandirte'] : ['Release inherited guilt when receiving', 'Regulate your nervous system when money appears', 'Feel safe as you expand']).map((item, i) => <li key={i} className="flex items-start gap-2 text-xs md:text-sm text-[#D4C4A8]" data-id="element-73">
                            <span className="text-[#E8B86D] mt-0.5" data-id="element-74">•</span>
                            <span data-id="element-75">{item}</span>
                          </li>)}
                      </ul>
                      <p className="text-xs md:text-sm text-[#E8D5B5] font-semibold" data-id="element-76">
                        {isEs ? 'No es teoría. Es integración emocional real.' : 'Not theory. Real emotional integration.'}
                      </p>
                      <p className="text-xs md:text-sm text-[#D4C4A8] leading-relaxed italic" data-id="element-77">
                        {isEs ? 'Escúchalo cada vez que una oportunidad te incomode… o cuando estés a punto de crecer y algo en ti se contraiga.' : "Listen to it every time an opportunity makes you uncomfortable… or when you're about to grow and something in you contracts."}
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-2 mb-4" data-id="element-78">
              <p className="text-[#FBF8F3]/60 text-xs md:text-sm" data-id="element-79">
                {isEs ? 'Antes' : 'Before'}{' '}
                <span className="line-through" data-id="element-80">$17</span>
              </p>
              <div className="bg-gradient-to-br from-[#E8B86D] via-[#D4A574] to-[#C87941] rounded-lg px-3 py-1" data-id="element-81">
                <span className="text-xl md:text-2xl font-bold text-[#2C4A3E]" data-id="element-82">
                  $7.97
                </span>
                <span className="text-xs text-[#2C4A3E] ml-1" data-id="element-83">USD</span>
              </div>
            </div>

            {/* Checkbox CTA */}
            <motion.button whileTap={{
            scale: 0.98
          }} onClick={() => onCheckedChange(!checked)} animate={checked ? {
            scale: 1
          } : {
            scale: [1, 1.01, 1]
          }} transition={checked ? {} : {
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all duration-300 text-left relative overflow-hidden ${checked ? 'border-[#E8B86D] bg-[#E8B86D]/15 shadow-[0_0_16px_rgba(232,184,109,0.25)]' : 'border-[#D4A574]/40 bg-[#1A3028]/50 hover:border-[#E8B86D]/60 hover:bg-[#E8B86D]/5'}`} data-id="element-84">
              {/* Shimmer effect when unchecked */}
              {!checked && <motion.div className="absolute inset-0 pointer-events-none" animate={{
              x: ['-100%', '200%']
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear',
              repeatDelay: 1.5
            }} style={{
              background: 'linear-gradient(90deg, transparent, rgba(232,184,109,0.08), transparent)'
            }} data-id="element-85" />}

              <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${checked ? 'bg-[#E8B86D] border-[#E8B86D]' : 'border-[#D4A574]/60 bg-transparent'}`} data-id="element-86">
                {checked && <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" data-id="element-87">
                    <polyline points="2 6 5 9 10 3" stroke="#2C4A3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-88" />
                  </svg>}
              </div>

              <div className="flex-1 flex items-center gap-2" data-id="element-89">
                {!checked && <HeadphonesIcon className="w-4 h-4 text-[#E8B86D] flex-shrink-0" data-id="element-90" />}
                <span className={`text-xs md:text-sm font-semibold leading-snug ${checked ? 'text-[#E8B86D]' : 'text-[#FBF8F3]/80'}`} data-id="element-91">
                  {checked ? isEs ? '✓ ¡Añadido a tu pedido!' : '✓ Added to your order!' : isEs ? 'Sí, quiero añadir el audio y acelerar mi proceso por sólo $7.97' : 'Yes, add this audio and accelerate my process for only $7.97'}
                </span>
              </div>
            </motion.button>
          </div>
        </div>
      </div>
    </div>;
}